import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

import { ApiError } from './api';
import type {
  AdminApi,
  AdminFacsimile,
  ContractorProfile,
  ContractorVehicleLink,
  DuplicateCandidate,
  TemporaryPasswordIssued,
  UserSummary,
  VehicleProfile,
} from './admin-api';
import {
  confirmAdminNavigation,
  getUnsavedLeaveMessage,
  registerAdminLeaveGuard,
} from './admin-leave-guard';
import {
  formatYekaterinburg,
  masterStatusLabel,
  mustChangePasswordLabel,
  problemMessage,
  roleLabel,
  roleLabels,
  userStatusLabel,
  duplicateFieldLabel,
} from './admin-presentation';
import {
  attributesPreserved,
  contractorBusinessLabel,
  emptyToNull,
  readContractorPhone,
  readContractorRepresentative,
  readVehicleColor,
  readVehicleModel,
  splitAliases,
  vehicleBusinessLabel,
  vehicleIdentityLabel,
  writeContractorContactAttributes,
  writeVehicleColor,
  writeVehicleModel,
} from './administration-presentation';
import { StatusChip, statusToneClass } from './admin-ui';
import { navigate } from './path-router';
import { TrustedContractorEmailsAdmin } from './trusted-contractor-emails-admin';

const DASH = '—';
const STALE_UI_MESSAGE =
  'Эту запись изменили после открытия. Обновите данные и повторите сохранение';
const FORBIDDEN_UI_MESSAGE = 'Нет прав для выполнения действия';

type MasterStatusFilter = 'ALL' | 'ACTIVE' | 'ARCHIVED';
type UserStatusFilter = 'ALL' | 'ACTIVE' | 'DISABLED';

type ContractorListFilters = {
  readonly query: string;
  readonly status: MasterStatusFilter;
};

type VehicleListFilters = {
  readonly query: string;
  readonly status: MasterStatusFilter;
};

type UserListFilters = {
  readonly query: string;
  readonly status: UserStatusFilter;
  readonly role: string;
};

const contractorFilterSession: {
  draft: ContractorListFilters;
  applied: ContractorListFilters;
} = {
  draft: { query: '', status: 'ALL' },
  applied: { query: '', status: 'ALL' },
};

const vehicleFilterSession: {
  draft: VehicleListFilters;
  applied: VehicleListFilters;
} = {
  draft: { query: '', status: 'ALL' },
  applied: { query: '', status: 'ALL' },
};

const userFilterSession: {
  draft: UserListFilters;
  applied: UserListFilters;
} = {
  draft: { query: '', status: 'ALL', role: '' },
  applied: { query: '', status: 'ALL', role: '' },
};

function displayOrDash(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : DASH;
}

function compareRu(a: string, b: string): number {
  return a.localeCompare(b, 'ru', { sensitivity: 'base', numeric: true });
}

function matchIncludes(haystack: string | null | undefined, needle: string): boolean {
  if (needle.trim().length === 0) {
    return true;
  }
  return (haystack ?? '').toLocaleLowerCase('ru').includes(needle.trim().toLocaleLowerCase('ru'));
}

function duplicateReasonLabel(fields: readonly string[]): string {
  return fields.map((field) => duplicateFieldLabel(field)).join(', ');
}

function isForbidden(failure: unknown): boolean {
  return failure instanceof ApiError && failure.problem.code === 'FORBIDDEN';
}

function isNotFound(failure: unknown): boolean {
  return failure instanceof ApiError && failure.problem.code === 'RESOURCE_NOT_FOUND';
}

function isStale(failure: unknown): boolean {
  return failure instanceof ApiError && failure.problem.code === 'STALE_VERSION';
}

function PackageProblemAlert(props: { readonly failure: unknown }): ReactElement {
  if (typeof props.failure === 'string') {
    return (
      <div role="alert" className="banner banner-closed">
        <p>{props.failure}</p>
      </div>
    );
  }
  let message = problemMessage(props.failure);
  if (isStale(props.failure)) {
    message = STALE_UI_MESSAGE;
  } else if (isForbidden(props.failure)) {
    message = FORBIDDEN_UI_MESSAGE;
  }
  return (
    <div role="alert" className="banner banner-closed">
      <p>{message}</p>
    </div>
  );
}

function useUnsavedLeaveGuard(isDirty: boolean): void {
  useEffect(() => {
    return registerAdminLeaveGuard(() => isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) {
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = getUnsavedLeaveMessage();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [isDirty]);
}

function guardedNavigate(path: string, mode: 'push' | 'replace' = 'push'): void {
  if (!confirmAdminNavigation()) {
    return;
  }
  navigate(path, mode);
}

function RegistryToolbar(props: {
  readonly title: string;
  readonly description: string;
  readonly createLabel: string;
  readonly createPath: string;
}): ReactElement {
  return (
    <div className="pkg-a-toolbar">
      <div>
        <h1>{props.title}</h1>
        <p className="page-description">{props.description}</p>
      </div>
      <button
        type="button"
        className="btn-primary pkg-a-create"
        onClick={() => navigate(props.createPath)}
      >
        {props.createLabel}
      </button>
    </div>
  );
}

function isPlainPrimaryClick(event: ReactMouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !event.defaultPrevented
  );
}

/** SPA registry cell link: plain left-click navigates; modifiers keep native <a> behavior. */
function RegistryLink(props: {
  readonly href: string;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <a
      href={props.href}
      className="pkg-a-registry-link"
      onClick={(event) => {
        if (!isPlainPrimaryClick(event)) {
          return;
        }
        event.preventDefault();
        if (!confirmAdminNavigation()) {
          return;
        }
        navigate(props.href);
      }}
    >
      {props.children}
    </a>
  );
}

function FilterBar(props: {
  readonly children: ReactNode;
  readonly onApply: () => void;
  readonly onReset: () => void;
}): ReactElement {
  return (
    <form
      className="pkg-a-filter-bar"
      onSubmit={(event) => {
        event.preventDefault();
        props.onApply();
      }}
    >
      <div className="pkg-a-filter-fields">{props.children}</div>
      <div className="pkg-a-filter-actions">
        <button type="submit">Найти</button>
        <button type="button" className="btn-ghost" onClick={props.onReset}>
          Сбросить
        </button>
      </div>
    </form>
  );
}

function RegistryState(props: {
  readonly loading: boolean;
  readonly error: unknown;
  readonly empty: boolean;
  readonly filteredEmpty: boolean;
  readonly onRetry: () => void;
  readonly onResetFilters?: () => void;
}): ReactElement | null {
  if (props.loading) {
    return <p role="status">Загрузка…</p>;
  }
  if (props.error !== null) {
    if (isForbidden(props.error)) {
      return (
        <div role="alert" className="banner banner-closed">
          <p>{FORBIDDEN_UI_MESSAGE}</p>
        </div>
      );
    }
    return (
      <div role="alert" className="banner banner-closed">
        <p>Не удалось загрузить данные</p>
        <PackageProblemAlert failure={props.error} />
        <button type="button" onClick={props.onRetry}>
          Повторить
        </button>
      </div>
    );
  }
  if (props.empty) {
    return <p role="status">Записей пока нет</p>;
  }
  if (props.filteredEmpty) {
    return (
      <div role="status" className="pkg-a-empty-filter">
        <p>По выбранным фильтрам ничего не найдено</p>
        {props.onResetFilters !== undefined ? (
          <button type="button" className="btn-ghost" onClick={props.onResetFilters}>
            Сбросить
          </button>
        ) : null}
      </div>
    );
  }
  return null;
}

function NotFoundCard(props: { readonly listPath: string; readonly listLabel: string }): ReactElement {
  return (
    <div className="pkg-a-card" role="alert">
      <p>Запись не найдена</p>
      <button type="button" className="back-link" onClick={() => navigate(props.listPath)}>
        {props.listLabel}
      </button>
    </div>
  );
}

function FormActions(props: {
  readonly primaryLabel: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly cancelLabel?: string;
  readonly disabled?: boolean;
  readonly archiveLabel?: string;
  readonly archiveBusy?: boolean;
  readonly onArchive?: () => void;
}): ReactElement {
  const showArchive = props.onArchive !== undefined && props.archiveLabel !== undefined;
  return (
    <div className="pkg-a-form-actions">
      <button type="submit" disabled={props.busy || props.disabled === true}>
        {props.busy ? 'Сохранение…' : props.primaryLabel}
      </button>
      <button type="button" className="btn-ghost" disabled={props.busy} onClick={props.onCancel}>
        {props.cancelLabel ?? 'Отменить'}
      </button>
      {showArchive ? (
        <button
          type="button"
          className="btn-ghost pkg-a-archive-action"
          disabled={props.archiveBusy === true || props.busy}
          onClick={props.onArchive}
        >
          {props.archiveLabel}
        </button>
      ) : null}
    </div>
  );
}

function contractorRegistryLabel(row: ContractorProfile): string {
  const name = row.legal_name.trim();
  return name.length > 0 ? name : 'Подрядчик без наименования';
}

function userRegistryLabel(user: UserSummary): string {
  const name = user.display_name.trim();
  if (name.length > 0) {
    return name;
  }
  const login = user.username.trim();
  return login.length > 0 ? login : 'Пользователь без имени';
}

function vehicleModelRegistryLabel(row: VehicleProfile): string {
  const model = readVehicleModel(row.attributes).trim();
  return model.length > 0 ? model : 'ТС без указанной модели';
}

function vehicleLastDecisionLabel(row: VehicleProfile): string {
  return row.last_signed_decision?.label ?? 'Новый';
}

function SuccessBanner(props: { readonly children: ReactNode }): ReactElement {
  return (
    <div role="status" className="banner banner-open">
      {props.children}
    </div>
  );
}

function RoleSelector(props: {
  readonly id: string;
  readonly label: string;
  readonly roles: readonly { readonly code: string; readonly name: string }[];
  readonly selected: readonly string[];
  readonly onChange: (next: readonly string[]) => void;
  readonly disabled?: boolean;
}): ReactElement {
  return (
    <fieldset className="role-selector pkg-a-role-selector" disabled={props.disabled === true}>
      <legend>{props.label}</legend>
      {props.roles.length === 0 ? (
        <p className="meta">Список ролей недоступен. Повторите загрузку.</p>
      ) : null}
      <div className="role-selector-grid pkg-a-role-selector-grid">
        {props.roles.map((role) => {
          const checked = props.selected.includes(role.code);
          const inputId = `${props.id}_${role.code}`;
          const localized = roleLabel(role.code);
          const labelText =
            localized !== role.code
              ? localized
              : role.name.trim().length > 0
                ? role.name.trim()
                : role.code;
          return (
            <label key={role.code} htmlFor={inputId} className="role-option pkg-a-role-option">
              <span className="role-option-check">
                <input
                  id={inputId}
                  type="checkbox"
                  value={role.code}
                  checked={checked}
                  onChange={() => {
                    props.onChange(
                      checked
                        ? props.selected.filter((code) => code !== role.code)
                        : [...props.selected, role.code],
                    );
                  }}
                />
              </span>
              <span className="role-option-label">{labelText}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function SecretOncePanel(props: {
  readonly title: string;
  readonly username: string;
  readonly temporaryPassword: string;
  readonly onContinue?: () => void;
  readonly continueLabel?: string;
  readonly onDismiss: () => void;
}): ReactElement {
  return (
    <div role="dialog" aria-labelledby="secret-title" className="banner pkg-a-secret">
      <h2 id="secret-title">{props.title}</h2>
      <p>Одноразовый пароль</p>
      <p className="pkg-a-secret-warn">Показывается один раз. После закрытия восстановить нельзя.</p>
      <p>Пользователь: {props.username}</p>
      <p className="pkg-a-secret-value">
        <code>{props.temporaryPassword}</code>
      </p>
      <div className="actions">
        {props.onContinue !== undefined ? (
          <button type="button" onClick={props.onContinue}>
            {props.continueLabel ?? 'Перейти к пользователю'}
          </button>
        ) : null}
        <button type="button" className="btn-ghost" onClick={props.onDismiss}>
          Закрыть
        </button>
      </div>
    </div>
  );
}

function parseSegment(
  path: string,
  base: string,
): { kind: 'list' } | { kind: 'new' } | { kind: 'detail'; id: string } {
  if (path === base) {
    return { kind: 'list' };
  }
  if (path === `${base}/new`) {
    return { kind: 'new' };
  }
  if (path.startsWith(`${base}/`)) {
    const id = path.slice(base.length + 1);
    if (id.length > 0 && !id.includes('/')) {
      return { kind: 'detail', id };
    }
  }
  return { kind: 'list' };
}

/* -------------------------------------------------------------------------- */
/* Users                                                                      */
/* -------------------------------------------------------------------------- */

function UsersSectionTabs(props: {
  readonly active: 'accounts' | 'trusted-emails';
}): ReactElement {
  return (
    <div className="section-tabs pkg-a-tabs" role="tablist" aria-label="Разделы пользователей">
      <button
        type="button"
        role="tab"
        aria-selected={props.active === 'accounts'}
        className={
          props.active === 'accounts'
            ? 'section-tab is-active nav-link nav-current'
            : 'section-tab nav-link'
        }
        onClick={() => guardedNavigate('/admin/users')}
      >
        Учётные записи
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={props.active === 'trusted-emails'}
        className={
          props.active === 'trusted-emails'
            ? 'section-tab is-active nav-link nav-current'
            : 'section-tab nav-link'
        }
        onClick={() => guardedNavigate('/admin/users/trusted-emails')}
      >
        Доверенные адреса подрядчиков
      </button>
    </div>
  );
}

export function UsersAdmin(props: { readonly api: AdminApi; readonly path: string }): ReactElement {
  const isTrustedEmails =
    props.path === '/admin/users/trusted-emails' ||
    props.path.startsWith('/admin/users/trusted-emails/');
  if (isTrustedEmails) {
    return (
      <div className="pkg-a-page" data-testid="users-admin-shell">
        <div className="pkg-a-toolbar">
          <div>
            <h1>Пользователи</h1>
            <p className="page-description">Учётные записи и права доступа</p>
          </div>
        </div>
        <UsersSectionTabs active="trusted-emails" />
        <TrustedContractorEmailsAdmin api={props.api} />
      </div>
    );
  }
  const segment = parseSegment(props.path, '/admin/users');
  if (segment.kind === 'new') {
    return <UserCreate api={props.api} />;
  }
  if (segment.kind === 'detail') {
    return <UserDetail api={props.api} userId={segment.id} />;
  }
  return <UserList api={props.api} />;
}

function UserList(props: { readonly api: AdminApi }): ReactElement {
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [roles, setRoles] = useState<readonly { readonly code: string; readonly name: string }[]>(
    [],
  );
  const [draft, setDraft] = useState<UserListFilters>(userFilterSession.draft);
  const [applied, setApplied] = useState<UserListFilters>(userFilterSession.applied);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const seq = useRef(0);

  const reload = () => {
    const requestSeq = seq.current + 1;
    seq.current = requestSeq;
    setLoading(true);
    setError(null);
    void Promise.all([props.api.listUsers(), props.api.listRoles()])
      .then(([userRows, roleRows]) => {
        if (seq.current !== requestSeq) {
          return;
        }
        setUsers(Array.isArray(userRows) ? userRows : []);
        setRoles(Array.isArray(roleRows) ? roleRows : []);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        if (seq.current !== requestSeq) {
          return;
        }
        setError(failure);
        setLoading(false);
      });
  };

  useEffect(() => {
    reload();
  }, [props.api]);

  const applyFilters = () => {
    userFilterSession.draft = draft;
    userFilterSession.applied = draft;
    setApplied(draft);
  };

  const resetFilters = () => {
    const empty: UserListFilters = { query: '', status: 'ALL', role: '' };
    userFilterSession.draft = empty;
    userFilterSession.applied = empty;
    setDraft(empty);
    setApplied(empty);
  };

  const filtered = users
    .filter((user) => {
      if (applied.status !== 'ALL' && user.status !== applied.status) {
        return false;
      }
      if (applied.role.length > 0 && !user.role_codes.includes(applied.role)) {
        return false;
      }
      if (applied.query.trim().length === 0) {
        return true;
      }
      return (
        matchIncludes(user.display_name, applied.query) ||
        matchIncludes(user.username, applied.query)
      );
    })
    .slice()
    .sort((a, b) => {
      const byName = compareRu(a.display_name, b.display_name);
      return byName !== 0 ? byName : compareRu(a.username, b.username);
    });

  const state = (
    <RegistryState
      loading={loading}
      error={error}
      empty={!loading && error === null && users.length === 0}
      filteredEmpty={!loading && error === null && users.length > 0 && filtered.length === 0}
      onRetry={reload}
      onResetFilters={resetFilters}
    />
  );

  return (
    <div className="pkg-a-page" data-testid="users-admin-shell">
      <RegistryToolbar
        title="Пользователи"
        description="Учётные записи и права доступа"
        createLabel="Создать пользователя"
        createPath="/admin/users/new"
      />
      <UsersSectionTabs active="accounts" />
      <FilterBar onApply={applyFilters} onReset={resetFilters}>
        <label htmlFor="user_filter_query" className="pkg-a-filter-query">
          Поиск
          <input
            id="user_filter_query"
            value={draft.query}
            onChange={(event) => setDraft({ ...draft, query: event.target.value })}
            placeholder="Имя или логин"
          />
        </label>
        <label htmlFor="user_filter_status">
          Статус
          <select
            id="user_filter_status"
            value={draft.status}
            onChange={(event) =>
              setDraft({ ...draft, status: event.target.value as UserStatusFilter })
            }
          >
            <option value="ALL">Все</option>
            <option value="ACTIVE">Активные</option>
            <option value="DISABLED">Отключённые</option>
          </select>
        </label>
        <label htmlFor="user_filter_role">
          Роль
          <select
            id="user_filter_role"
            value={draft.role}
            onChange={(event) => setDraft({ ...draft, role: event.target.value })}
          >
            <option value="">Все роли</option>
            {roles.map((role) => (
              <option key={role.code} value={role.code}>
                {role.name.trim().length > 0 ? role.name : roleLabel(role.code)}
              </option>
            ))}
          </select>
        </label>
      </FilterBar>
      {state}
      {!loading && error === null && filtered.length > 0 ? (
        <div className="table-wrap">
          <table className="data-table users-table pkg-a-table">
            <thead>
              <tr>
                <th scope="col">Имя</th>
                <th scope="col">Логин</th>
                <th scope="col">Роли</th>
                <th scope="col">Статус</th>
                <th scope="col">Смена пароля</th>
                <th scope="col">Действие</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((user) => (
                <tr key={user.id}>
                  <td>
                    <RegistryLink href={`/admin/users/${user.id}`}>
                      {userRegistryLabel(user)}
                    </RegistryLink>
                  </td>
                  <td>{user.username}</td>
                  <td>{roleLabels(user.role_codes)}</td>
                  <td>
                    <StatusChip
                      label={userStatusLabel(user.status)}
                      tone={statusToneClass(user.status)}
                    />
                  </td>
                  <td>{mustChangePasswordLabel(user.must_change_password)}</td>
                  <td>
                    <RegistryLink href={`/admin/users/${user.id}`}>Открыть</RegistryLink>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function UserCreate(props: { readonly api: AdminApi }): ReactElement {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleCodes, setRoleCodes] = useState<readonly string[]>([]);
  const [roles, setRoles] = useState<readonly { readonly code: string; readonly name: string }[]>(
    [],
  );
  const [rolesError, setRolesError] = useState<unknown>(null);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [issued, setIssued] = useState<TemporaryPasswordIssued | null>(null);
  const dirty =
    issued === null &&
    (username.trim().length > 0 || displayName.trim().length > 0 || roleCodes.length > 0);
  useUnsavedLeaveGuard(dirty);

  useEffect(() => {
    setRolesLoading(true);
    void props.api
      .listRoles()
      .then((rows) => {
        setRoles(Array.isArray(rows) ? rows : []);
        setRolesError(null);
        setRolesLoading(false);
      })
      .catch((failure: unknown) => {
        setRoles([]);
        setRolesError(failure);
        setRolesLoading(false);
      });
  }, [props.api]);

  if (issued !== null) {
    return (
      <div className="pkg-a-page">
        <SuccessBanner>
          <p>Пользователь создан</p>
        </SuccessBanner>
        <SecretOncePanel
          title="Пользователь создан"
          username={issued.user.username}
          temporaryPassword={issued.temporary_password}
          onContinue={() => {
            const target = issued.user.id;
            setIssued(null);
            navigate(`/admin/users/${target}`);
          }}
          onDismiss={() => {
            setIssued(null);
            navigate('/admin/users');
          }}
        />
      </div>
    );
  }

  const rolesReady = !rolesLoading && rolesError === null && roles.length > 0;

  return (
    <div className="pkg-a-page">
      <button type="button" className="back-link" onClick={() => guardedNavigate('/admin/users')}>
        ← К пользователям
      </button>
      <h1>Новый пользователь</h1>
      {error !== null ? <PackageProblemAlert failure={error} /> : null}
      {rolesError !== null ? (
        <div role="alert" className="banner banner-closed">
          <p>Не удалось загрузить роли. Создание недоступно.</p>
          <PackageProblemAlert failure={rolesError} />
        </div>
      ) : null}
      <form
        className="pkg-a-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (busy || !rolesReady) {
            return;
          }
          setBusy(true);
          setError(null);
          void props.api
            .onboardUser({ username, displayName, roleCodes })
            .then((result) => {
              setIssued(result);
              setBusy(false);
            })
            .catch((failure: unknown) => {
              setError(failure);
              setBusy(false);
            });
        }}
      >
        <div className="pkg-a-form-grid">
          <label htmlFor="new_username">
            Имя пользователя (логин)
            <input
              id="new_username"
              value={username}
              autoComplete="off"
              onChange={(event) => setUsername(event.target.value)}
            />
          </label>
          <label htmlFor="new_display_name">
            Отображаемое имя
            <input
              id="new_display_name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </label>
        </div>
        <RoleSelector
          id="new_role"
          label="Роли пользователя"
          roles={roles}
          selected={roleCodes}
          onChange={setRoleCodes}
          disabled={!rolesReady || busy}
        />
        <FormActions
          primaryLabel="Создать"
          busy={busy}
          disabled={!rolesReady}
          onCancel={() => guardedNavigate('/admin/users')}
        />
      </form>
    </div>
  );
}

function UserDetail(props: { readonly api: AdminApi; readonly userId: string }): ReactElement {
  const [user, setUser] = useState<UserSummary | null>(null);
  const [rolesCatalog, setRolesCatalog] = useState<
    readonly { readonly code: string; readonly name: string }[]
  >([]);
  const [selectedRoles, setSelectedRoles] = useState<readonly string[]>([]);
  const [facsimile, setFacsimile] = useState<AdminFacsimile | null>(null);
  const [facsimileMissing, setFacsimileMissing] = useState(false);
  const [issued, setIssued] = useState<TemporaryPasswordIssued | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rolesBusy, setRolesBusy] = useState(false);
  const [accessBusy, setAccessBusy] = useState(false);
  const [facsimileBusy, setFacsimileBusy] = useState(false);
  const [rolesSaved, setRolesSaved] = useState(false);
  const loadSeq = useRef(0);
  const userVersionRef = useRef<string>('');

  useEffect(() => {
    userVersionRef.current = user?.version ?? '';
  }, [user]);

  const currentRoleCodes = Array.isArray(user?.role_codes) ? user.role_codes : [];
  const selectedRoleCodes = Array.isArray(selectedRoles) ? selectedRoles : [];
  const rolesDirty =
    user !== null &&
    (selectedRoleCodes.length !== currentRoleCodes.length ||
      selectedRoleCodes.some((code) => !currentRoleCodes.includes(code)));
  useUnsavedLeaveGuard(rolesDirty);

  useEffect(() => {
    void props.api
      .listRoles()
      .then((rows) => setRolesCatalog(Array.isArray(rows) ? rows : []))
      .catch((failure: unknown) => setError(failure));
  }, [props.api]);

  useEffect(() => {
    const seq = loadSeq.current + 1;
    loadSeq.current = seq;
    const targetId = props.userId;
    setUser(null);
    setFacsimile(null);
    setFacsimileMissing(false);
    setIssued(null);
    setError(null);
    setNotFound(false);
    setLoading(true);
    setRolesSaved(false);
    void props.api
      .getUser(targetId)
      .then((next) => {
        if (loadSeq.current !== seq) {
          return;
        }
        setUser(next);
        setSelectedRoles(Array.isArray(next.role_codes) ? next.role_codes : []);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        if (loadSeq.current !== seq) {
          return;
        }
        if (isNotFound(failure)) {
          setNotFound(true);
        } else {
          setError(failure);
        }
        setLoading(false);
      });
    void props.api
      .getUserFacsimile(targetId)
      .then((next) => {
        if (loadSeq.current !== seq) {
          return;
        }
        setFacsimile(next);
        setFacsimileMissing(false);
      })
      .catch((failure: unknown) => {
        if (loadSeq.current !== seq) {
          return;
        }
        if (isNotFound(failure)) {
          setFacsimile(null);
          setFacsimileMissing(true);
          return;
        }
        setError(failure);
      });
  }, [props.api, props.userId]);

  if (loading) {
    return <p role="status">Загрузка…</p>;
  }
  if (notFound) {
    return <NotFoundCard listPath="/admin/users" listLabel="К списку" />;
  }
  if (user === null) {
    return error !== null ? <PackageProblemAlert failure={error} /> : <p role="status">Загрузка…</p>;
  }

  const goBack = () => guardedNavigate('/admin/users');

  return (
    <div className="pkg-a-page">
      <button type="button" className="back-link" onClick={goBack}>
        ← К пользователям
      </button>
      <div className="pkg-a-detail-header">
        <div>
          <h1>{user.display_name}</h1>
          <p className="meta">
            {user.username} · {mustChangePasswordLabel(user.must_change_password)}
          </p>
        </div>
        <StatusChip
          label={userStatusLabel(user.status ?? '')}
          tone={statusToneClass(user.status ?? '')}
        />
      </div>
      {error !== null ? <PackageProblemAlert failure={error} /> : null}
      {rolesSaved ? (
        <SuccessBanner>
          <p>Изменения сохранены</p>
        </SuccessBanner>
      ) : null}
      {issued !== null ? (
        <SecretOncePanel
          title="Одноразовый пароль"
          username={issued.user.username}
          temporaryPassword={issued.temporary_password}
          onDismiss={() => setIssued(null)}
        />
      ) : null}

      <section className="pkg-a-section" aria-labelledby="user-roles-title">
        <h2 id="user-roles-title">Роли</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (rolesBusy) {
              return;
            }
            const targetId = user.id;
            const expected = userVersionRef.current || user.version;
            setRolesBusy(true);
            setRolesSaved(false);
            setError(null);
            void props.api
              .replaceRoles(targetId, expected, selectedRoleCodes)
              .then((next) => {
                const saved = next as UserSummary;
                if (typeof saved.version === 'string') {
                  userVersionRef.current = saved.version;
                }
                setUser(saved);
                setSelectedRoles(
                  Array.isArray(saved.role_codes) ? saved.role_codes : selectedRoleCodes,
                );
                setRolesSaved(true);
                setRolesBusy(false);
              })
              .catch((failure: unknown) => {
                setError(failure);
                setRolesBusy(false);
              });
        }}
      >
          <RoleSelector
            id="edit_role"
            label="Роли пользователя"
            roles={rolesCatalog}
            selected={selectedRoleCodes}
          onChange={(next) => {
              setSelectedRoles(next);
              setRolesSaved(false);
            }}
            disabled={rolesBusy}
          />
          <FormActions
            primaryLabel="Сохранить роли"
            busy={rolesBusy}
            cancelLabel="Отменить"
            onCancel={() => {
              if (rolesDirty && !window.confirm(getUnsavedLeaveMessage())) {
                return;
              }
              setSelectedRoles(currentRoleCodes);
              setRolesSaved(false);
              setError(null);
            }}
          />
        </form>
      </section>

      <section className="pkg-a-section" aria-labelledby="user-access-title">
        <h2 id="user-access-title">Доступ</h2>
        <div className="pkg-a-form-actions">
          <button
            type="button"
            disabled={accessBusy}
            onClick={() => {
              const nextStatus = user.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
              const actionLabel = nextStatus === 'DISABLED' ? 'отключить' : 'активировать';
              if (
                !window.confirm(
                  `Подтвердите: ${actionLabel} пользователя «${user.display_name}»?`,
                )
              ) {
                return;
              }
              const targetId = user.id;
              const expected = userVersionRef.current || user.version;
              setAccessBusy(true);
              setError(null);
              void props.api
                .setStatus(targetId, expected, nextStatus)
                .then((next) => {
                  if (user.id !== targetId) {
                    return;
                  }
                  if (typeof next.version === 'string') {
                    userVersionRef.current = next.version;
                  }
                  setUser(next);
                  setAccessBusy(false);
                })
                .catch((failure: unknown) => {
                  setError(failure);
                  setAccessBusy(false);
                });
            }}
          >
            {user.status === 'ACTIVE' ? 'Отключить' : 'Активировать'}
          </button>
          <button
            type="button"
            disabled={accessBusy}
            onClick={() => {
              if (
                !window.confirm(
                  `Выдать новый одноразовый пароль пользователю «${user.display_name}»? Предыдущий временный пароль перестанет действовать.`,
                )
              ) {
                return;
              }
              const targetId = user.id;
              const expected = userVersionRef.current || user.version;
              setAccessBusy(true);
              setError(null);
              void props.api
                .issueTemporaryPassword(targetId, expected)
                .then((next) => {
                  if (user.id !== targetId) {
                    return;
                  }
                  if (typeof next.user.version === 'string') {
                    userVersionRef.current = next.user.version;
                  }
                  setIssued(next);
                  setUser(next.user);
                  setAccessBusy(false);
                })
                .catch((failure: unknown) => {
                  setError(failure);
                  setAccessBusy(false);
                });
            }}
          >
            Выдать новый одноразовый пароль
          </button>
        </div>
      </section>

      <section className="pkg-a-section" aria-labelledby="user-facsimile-title">
        <h2 id="user-facsimile-title">Факсимиле</h2>
        <p className="meta">
          {facsimileMissing || facsimile === null || !Array.isArray(facsimile.versions)
            ? 'Не загружено'
            : 'Загружено'}
        </p>
        {facsimile !== null &&
        Array.isArray(facsimile.versions) &&
        facsimile.versions.length > 0 ? (
          <ul className="pkg-a-facsimile-list">
            {facsimile.versions.map((version) => (
              <li key={version.id}>{formatYekaterinburg(version.created_at)}</li>
            ))}
          </ul>
        ) : null}
        <label htmlFor="facsimile_file">
          Новый файл факсимиле
          <input
            id="facsimile_file"
            type="file"
            disabled={facsimileBusy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file === undefined) {
                return;
              }
              const targetId = user.id;
              const seq = loadSeq.current;
              setFacsimileBusy(true);
              setError(null);
              void props.api
                .provisionFacsimile(targetId, facsimile?.version ?? null, file)
                .then(() => props.api.getUserFacsimile(targetId))
                .then((next) => {
                  if (loadSeq.current !== seq) {
                    return;
                  }
                  setFacsimile(next);
                  setFacsimileMissing(false);
                  setFacsimileBusy(false);
                })
                .catch((failure: unknown) => {
                  if (loadSeq.current !== seq) {
                    return;
                  }
                  setError(failure);
                  setFacsimileBusy(false);
                });
            }}
          />
        </label>
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Contractors                                                                */
/* -------------------------------------------------------------------------- */

export function ContractorAdmin(props: {
  readonly api: AdminApi;
  readonly path: string;
}): ReactElement {
  const segment = parseSegment(props.path, '/admin/contractors');
  if (segment.kind === 'new') {
    return <ContractorCreate api={props.api} />;
  }
  if (segment.kind === 'detail') {
    return <ContractorDetail api={props.api} profileId={segment.id} />;
  }
  return <ContractorList api={props.api} />;
}

function ContractorList(props: { readonly api: AdminApi }): ReactElement {
  const [rows, setRows] = useState<ContractorProfile[]>([]);
  const [draft, setDraft] = useState<ContractorListFilters>(contractorFilterSession.draft);
  const [applied, setApplied] = useState<ContractorListFilters>(contractorFilterSession.applied);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const seq = useRef(0);

  const reload = () => {
    const requestSeq = seq.current + 1;
    seq.current = requestSeq;
    setLoading(true);
    setError(null);
    void props.api
      .listContractorProfiles()
      .then((list) => {
        if (seq.current !== requestSeq) {
          return;
        }
        setRows(Array.isArray(list) ? list : []);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        if (seq.current !== requestSeq) {
          return;
        }
        setError(failure);
        setLoading(false);
      });
  };

  useEffect(() => {
    reload();
  }, [props.api]);

  const applyFilters = () => {
    contractorFilterSession.draft = draft;
    contractorFilterSession.applied = draft;
    setApplied(draft);
  };

  const resetFilters = () => {
    const empty: ContractorListFilters = { query: '', status: 'ALL' };
    contractorFilterSession.draft = empty;
    contractorFilterSession.applied = empty;
    setDraft(empty);
    setApplied(empty);
  };

  const filtered = rows
    .filter((row) => {
      if (applied.status !== 'ALL' && row.status !== applied.status) {
        return false;
      }
      if (applied.query.trim().length === 0) {
        return true;
      }
      return (
        matchIncludes(row.legal_name, applied.query) ||
        matchIncludes(row.inn, applied.query) ||
        row.aliases.some((alias) => matchIncludes(alias, applied.query))
      );
    })
    .slice()
    .sort((a, b) => {
      const byName = compareRu(a.legal_name, b.legal_name);
      return byName !== 0 ? byName : compareRu(a.id, b.id);
    });

  return (
    <div className="pkg-a-page">
      <RegistryToolbar
        title="Подрядчики"
        description="Организации, участвующие в допуске транспорта"
        createLabel="Создать подрядчика"
        createPath="/admin/contractors/new"
      />
      <FilterBar onApply={applyFilters} onReset={resetFilters}>
        <label htmlFor="contractor_filter_query" className="pkg-a-filter-query">
          Поиск
          <input
            id="contractor_filter_query"
            value={draft.query}
            onChange={(event) => setDraft({ ...draft, query: event.target.value })}
            placeholder="Наименование, ИНН, название"
          />
        </label>
        <label htmlFor="contractor_filter_status">
          Статус
          <select
            id="contractor_filter_status"
            value={draft.status}
            onChange={(event) =>
              setDraft({ ...draft, status: event.target.value as MasterStatusFilter })
            }
          >
            <option value="ALL">Все</option>
            <option value="ACTIVE">Активные</option>
            <option value="ARCHIVED">В архиве</option>
          </select>
        </label>
      </FilterBar>
      <RegistryState
        loading={loading}
        error={error}
        empty={!loading && error === null && rows.length === 0}
        filteredEmpty={!loading && error === null && rows.length > 0 && filtered.length === 0}
        onRetry={reload}
        onResetFilters={resetFilters}
      />
      {!loading && error === null && filtered.length > 0 ? (
        <div className="table-wrap">
          <table className="data-table pkg-a-table">
            <thead>
              <tr>
                <th scope="col">Наименование</th>
                <th scope="col">ИНН</th>
                <th scope="col">Контактное лицо</th>
                <th scope="col">Телефон</th>
                <th scope="col">Статус</th>
                <th scope="col">Действие</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  <td>
                    <RegistryLink href={`/admin/contractors/${row.id}`}>
                      {contractorRegistryLabel(row)}
                    </RegistryLink>
                  </td>
                  <td>{displayOrDash(row.inn)}</td>
                  <td>{displayOrDash(readContractorRepresentative(row.attributes))}</td>
                  <td>{displayOrDash(readContractorPhone(row.attributes))}</td>
                  <td>
                    <StatusChip
                      label={masterStatusLabel(row.status)}
                      tone={statusToneClass(row.status)}
                    />
                  </td>
                  <td>
                    <RegistryLink href={`/admin/contractors/${row.id}`}>Открыть</RegistryLink>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function ContractorCreate(props: { readonly api: AdminApi }): ReactElement {
  const [legalName, setLegalName] = useState('');
  const [inn, setInn] = useState('');
  const [aliases, setAliases] = useState('');
  const [representative, setRepresentative] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const mounted = useRef(true);
  const dirty =
    legalName.length > 0 ||
    inn.length > 0 ||
    aliases.length > 0 ||
    representative.length > 0 ||
    phone.length > 0;
  useUnsavedLeaveGuard(dirty && !busy);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return (
    <div className="pkg-a-page">
      <button
        type="button"
        className="back-link"
        onClick={() => guardedNavigate('/admin/contractors')}
      >
        ← К подрядчикам
      </button>
      <h1>Новый подрядчик</h1>
      {error !== null ? <PackageProblemAlert failure={error} /> : null}
      <form
        className="pkg-a-form"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          if (busy) {
            return;
          }
          setBusy(true);
          setError(null);
          void props.api
            .createContractorProfile({
              legal_name: legalName,
              inn: emptyToNull(inn),
              aliases: splitAliases(aliases),
              attributes: writeContractorContactAttributes({}, { representative, phone }),
            })
            .then((created) => {
              if (!mounted.current) {
                return;
              }
              navigate(`/admin/contractors/${created.id}`);
            })
            .catch((failure: unknown) => {
              if (!mounted.current) {
                return;
              }
              setError(failure);
              setBusy(false);
            });
        }}
      >
        <fieldset className="pkg-a-fieldset">
          <legend>Сведения об организации</legend>
          <div className="pkg-a-form-grid">
            <label htmlFor="contractor_legal_name">
              Юридическое наименование
              <input
                id="contractor_legal_name"
                value={legalName}
                onChange={(event) => setLegalName(event.target.value)}
              />
            </label>
            <label htmlFor="contractor_inn">
              ИНН
              <input
                id="contractor_inn"
                value={inn}
                onChange={(event) => setInn(event.target.value)}
              />
            </label>
            <label htmlFor="contractor_aliases" className="pkg-a-span-2">
              Дополнительные названия
              <input
                id="contractor_aliases"
                value={aliases}
                onChange={(event) => setAliases(event.target.value)}
                placeholder="Через запятую"
              />
            </label>
            <label htmlFor="contractor_representative">
              Контактное лицо
              <input
                id="contractor_representative"
                value={representative}
                onChange={(event) => setRepresentative(event.target.value)}
              />
            </label>
            <label htmlFor="contractor_phone">
              Телефон
              <input
                id="contractor_phone"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            </label>
          </div>
        </fieldset>
        <FormActions
          primaryLabel="Создать"
          busy={busy}
          onCancel={() => guardedNavigate('/admin/contractors')}
        />
      </form>
    </div>
  );
}

function ContractorDetail(props: {
  readonly api: AdminApi;
  readonly profileId: string;
}): ReactElement {
  const [profile, setProfile] = useState<ContractorProfile | null>(null);
  const [legalName, setLegalName] = useState('');
  const [inn, setInn] = useState('');
  const [aliases, setAliases] = useState('');
  const [representative, setRepresentative] = useState('');
  const [phone, setPhone] = useState('');
  const [duplicates, setDuplicates] = useState<DuplicateCandidate<ContractorProfile>[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const loadSeq = useRef(0);

  const applyProfile = (next: ContractorProfile) => {
    setProfile(next);
    setLegalName(next.legal_name);
    setInn(next.inn ?? '');
    setAliases(next.aliases.join(', '));
    setRepresentative(readContractorRepresentative(next.attributes));
    setPhone(readContractorPhone(next.attributes));
  };

  const dirty =
    profile !== null &&
    (legalName !== profile.legal_name ||
      inn !== (profile.inn ?? '') ||
      aliases !== profile.aliases.join(', ') ||
      representative !== readContractorRepresentative(profile.attributes) ||
      phone !== readContractorPhone(profile.attributes));
  useUnsavedLeaveGuard(dirty);

  useEffect(() => {
    const seq = loadSeq.current + 1;
    loadSeq.current = seq;
    const targetId = props.profileId;
    setProfile(null);
    setError(null);
    setDuplicates([]);
    setNotFound(false);
    setLoading(true);
    setSaved(false);
    void props.api
      .getContractorProfile(targetId)
      .then((next) => {
        if (loadSeq.current !== seq) {
          return;
        }
        applyProfile(next);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        if (loadSeq.current !== seq) {
          return;
        }
        if (isNotFound(failure)) {
          setNotFound(true);
        } else {
          setError(failure);
        }
        setLoading(false);
      });
    void props.api
      .contractorDuplicates(targetId)
      .then((rows) => {
        if (loadSeq.current !== seq) {
          return;
        }
        setDuplicates(Array.isArray(rows) ? rows : []);
      })
      .catch((failure: unknown) => {
        if (loadSeq.current !== seq) {
          return;
        }
        if (!isNotFound(failure)) {
          setError(failure);
        }
      });
  }, [props.api, props.profileId]);

  if (loading) {
    return <p role="status">Загрузка…</p>;
  }
  if (notFound) {
    return <NotFoundCard listPath="/admin/contractors" listLabel="К списку" />;
  }
  if (profile === null) {
    return error !== null ? <PackageProblemAlert failure={error} /> : <p role="status">Загрузка…</p>;
  }

  const resetDraft = () => {
    if (dirty && !window.confirm(getUnsavedLeaveMessage())) {
      return;
    }
    applyProfile(profile);
    setError(null);
    setSaved(false);
  };

  return (
    <div className="pkg-a-page">
      <button
        type="button"
        className="back-link"
        onClick={() => guardedNavigate('/admin/contractors')}
      >
        ← К подрядчикам
      </button>
      <div className="pkg-a-detail-header">
        <h1>{profile.legal_name}</h1>
        <StatusChip
          label={masterStatusLabel(profile.status)}
          tone={statusToneClass(profile.status)}
        />
      </div>
      {error !== null ? <PackageProblemAlert failure={error} /> : null}
      {saved ? (
        <SuccessBanner>
          <p>Изменения сохранены</p>
        </SuccessBanner>
      ) : null}
      <form
        className="pkg-a-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (busy) {
            return;
          }
          const targetId = profile.id;
          const expected = profile.version;
          const seq = loadSeq.current;
          const attributes = writeContractorContactAttributes(
            attributesPreserved(profile.attributes, null),
            { representative, phone },
          );
          setBusy(true);
          setSaved(false);
          setError(null);
          void props.api
            .updateContractorProfile(targetId, {
              expected_version: expected,
              legal_name: legalName,
              inn: emptyToNull(inn),
              aliases: splitAliases(aliases),
              attributes,
            })
            .then((next) => {
              if (loadSeq.current !== seq) {
                return;
              }
              applyProfile(next);
              setSaved(true);
              setBusy(false);
            })
            .catch((failure: unknown) => {
              if (loadSeq.current !== seq) {
                return;
              }
              setError(failure);
              setBusy(false);
            });
        }}
      >
        <fieldset className="pkg-a-fieldset">
          <legend>Сведения об организации</legend>
          <div className="pkg-a-form-grid">
            <label htmlFor="legal_name">
              Юридическое наименование
              <input
                id="legal_name"
                value={legalName}
                onChange={(event) => setLegalName(event.target.value)}
              />
            </label>
            <label htmlFor="inn">
              ИНН
              <input id="inn" value={inn} onChange={(event) => setInn(event.target.value)} />
            </label>
            <label htmlFor="aliases" className="pkg-a-span-2">
              Дополнительные названия
              <input
                id="aliases"
                value={aliases}
                onChange={(event) => setAliases(event.target.value)}
              />
            </label>
            <label htmlFor="representative">
              Контактное лицо
              <input
                id="representative"
                value={representative}
                onChange={(event) => setRepresentative(event.target.value)}
              />
            </label>
            <label htmlFor="phone">
              Телефон
              <input id="phone" value={phone} onChange={(event) => setPhone(event.target.value)} />
            </label>
          </div>
        </fieldset>
        <FormActions
          primaryLabel="Сохранить изменения"
          busy={busy}
          cancelLabel="Отменить изменения"
          onCancel={resetDraft}
          archiveLabel={profile.status !== 'ARCHIVED' ? 'Архивировать' : undefined}
          archiveBusy={archiveBusy}
          onArchive={
            profile.status !== 'ARCHIVED'
              ? () => {
                  if (
                    !window.confirm(
                      `Архивировать подрядчика «${profile.legal_name}»? Связанные ТС не будут удалены.`,
                    )
                  ) {
                    return;
                  }
                  const targetId = profile.id;
                  const expected = profile.version;
                  const seq = loadSeq.current;
                  setArchiveBusy(true);
                  setError(null);
                  void props.api
                    .archiveContractorProfile(targetId, expected)
                    .then((next) => {
                      if (loadSeq.current !== seq) {
                        return;
                      }
                      applyProfile(next);
                      setArchiveBusy(false);
                    })
                    .catch((failure: unknown) => {
                      if (loadSeq.current !== seq) {
                        return;
                      }
                      setError(failure);
                      setArchiveBusy(false);
                    });
                }
              : undefined
          }
        />
      </form>

      <ContractorVehicleLinksSection
        api={props.api}
        profile={profile}
        onProfileVersionRefresh={(next) => {
          setProfile(next);
        }}
        onError={setError}
      />

      {duplicates.length > 0 ? (
        <section className="pkg-a-section" aria-labelledby="contractor-duplicates-title">
          <h2 id="contractor-duplicates-title">Возможные дубликаты</h2>
          <ul>
            {duplicates.map((row) => (
              <li key={row.profile.id}>
                {contractorBusinessLabel(row.profile)} · {duplicateReasonLabel(row.matched_fields)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function ContractorVehicleLinksSection(props: {
  readonly api: AdminApi;
  readonly profile: ContractorProfile;
  readonly onProfileVersionRefresh: (next: ContractorProfile) => void;
  readonly onError: (failure: unknown) => void;
}): ReactElement {
  const [links, setLinks] = useState<ContractorVehicleLink[]>([]);
  const [vehicles, setVehicles] = useState<VehicleProfile[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [attachOpen, setAttachOpen] = useState(false);
  const [pendingUnlink, setPendingUnlink] = useState<{
    readonly link: ContractorVehicleLink;
    readonly vehicle: VehicleProfile | null;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = () => {
    void props.api
      .listContractorVehicleLinks(props.profile.id)
      .then((rows) => setLinks(Array.isArray(rows) ? rows : []))
      .catch((failure: unknown) => props.onError(failure));
    void props.api
      .listVehicleProfiles()
      .then((rows) => setVehicles(Array.isArray(rows) ? rows : []))
      .catch((failure: unknown) => props.onError(failure));
  };

  useEffect(() => {
    reload();
  }, [props.api, props.profile.id]);

  const vehicleById = new Map(vehicles.map((row) => [row.id, row]));
  const activeLinks = links.filter((row) => row.status === 'ACTIVE');
  const archivedLinks = links.filter((row) => row.status === 'ARCHIVED');
  const linkedActiveIds = new Set(activeLinks.map((row) => row.vehicle_profile_id));
  const attachCandidates = vehicles.filter(
    (row) => row.status === 'ACTIVE' && !linkedActiveIds.has(row.id),
  );

  const attach = () => {
    if (selectedVehicleId.length === 0) {
      props.onError('Выберите ТС для закрепления.');
      return;
    }
    setBusy(true);
    void props.api
      .createContractorVehicleLink(props.profile.id, {
        vehicle_profile_id: selectedVehicleId,
        expected_contractor_profile_version: props.profile.version,
      })
      .then(() => {
        setSelectedVehicleId('');
        setAttachOpen(false);
        return props.api.getContractorProfile(props.profile.id);
      })
      .then((next) => {
        props.onProfileVersionRefresh(next);
        reload();
      })
      .catch((failure: unknown) => props.onError(failure))
      .finally(() => setBusy(false));
  };

  const confirmUnlink = () => {
    if (pendingUnlink === null) {
      return;
    }
    const target = pendingUnlink.link;
    setBusy(true);
    void props.api
      .archiveContractorVehicleLink(props.profile.id, target.id, {
        expected_link_version: target.version,
      })
      .then(() => {
        setPendingUnlink(null);
        reload();
      })
      .catch((failure: unknown) => props.onError(failure))
      .finally(() => setBusy(false));
  };

  return (
    <section className="stack-gap pkg-a-section" aria-labelledby="contractor-vehicle-links-title">
      <h2 id="contractor-vehicle-links-title">Транспортные средства подрядчика</h2>
      {activeLinks.length === 0 ? (
        <p className="meta" role="status">
          Активных закреплений нет.
        </p>
      ) : (
        <div className="table-wrap">
          <table className="data-table pkg-a-table">
            <thead>
              <tr>
                <th scope="col">Госномер / идентификатор</th>
                <th scope="col">Марка / модель</th>
                <th scope="col">Состояние связи</th>
                <th scope="col">Действие</th>
              </tr>
            </thead>
            <tbody>
              {activeLinks.map((link) => {
                const vehicle = vehicleById.get(link.vehicle_profile_id) ?? null;
                const identity =
                  vehicle !== null ? vehicleBusinessLabel(vehicle) : 'ТС недоступно';
                const model =
                  vehicle !== null ? displayOrDash(readVehicleModel(vehicle.attributes)) : DASH;
                return (
                  <tr key={link.id}>
                    <td>{identity}</td>
                    <td>{model}</td>
                    <td>{masterStatusLabel(link.status)}</td>
                    <td>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setPendingUnlink({ link, vehicle })}
                      >
                        Открепить
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="actions">
        <button
          type="button"
          disabled={busy || props.profile.status === 'ARCHIVED'}
          onClick={() => setAttachOpen((open) => !open)}
        >
          Закрепить ТС
        </button>
      </div>
      {attachOpen ? (
        <div className="form-section">
          <label htmlFor="attach_vehicle">
            Активное ТС / оборудование
            <select
              id="attach_vehicle"
              aria-label="Активное ТС / оборудование"
              value={selectedVehicleId}
              onChange={(event) => setSelectedVehicleId(event.target.value)}
            >
              <option value="">Выберите…</option>
              {attachCandidates.map((row) => (
                <option key={row.id} value={row.id}>
                  {vehicleIdentityLabel(row)}
                </option>
              ))}
            </select>
          </label>
          {attachCandidates.length === 0 ? (
            <p className="meta">Нет доступных активных ТС для закрепления.</p>
          ) : null}
          <div className="actions">
            <button
              type="button"
              disabled={busy || selectedVehicleId.length === 0}
              onClick={attach}
            >
              Закрепить
            </button>
            <button type="button" disabled={busy} onClick={() => setAttachOpen(false)}>
              Отмена
            </button>
          </div>
        </div>
      ) : null}
      {pendingUnlink !== null ? (
        <div role="dialog" aria-labelledby="unlink-title" className="banner">
          <h3 id="unlink-title">Открепить ТС?</h3>
          <p>
            {pendingUnlink.vehicle !== null
              ? vehicleIdentityLabel(pendingUnlink.vehicle)
              : 'ТС недоступно'}
          </p>
          <div className="actions">
            <button type="button" disabled={busy} onClick={confirmUnlink}>
              Подтвердить открепление
            </button>
            <button type="button" disabled={busy} onClick={() => setPendingUnlink(null)}>
              Отмена
            </button>
          </div>
        </div>
      ) : null}
      <details className="pkg-a-history">
        <summary>История связей</summary>
        {archivedLinks.length === 0 ? (
          <p className="meta">Архивных связей нет.</p>
        ) : (
          <ul>
            {archivedLinks.map((link) => {
              const vehicle = vehicleById.get(link.vehicle_profile_id) ?? null;
              return (
                <li key={link.id}>
                  {vehicle !== null ? vehicleIdentityLabel(vehicle) : 'ТС недоступно'} ·{' '}
                  {masterStatusLabel(link.status)}
                </li>
              );
            })}
          </ul>
        )}
      </details>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Vehicles                                                                   */
/* -------------------------------------------------------------------------- */

export function VehicleAdmin(props: {
  readonly api: AdminApi;
  readonly path: string;
}): ReactElement {
  const segment = parseSegment(props.path, '/admin/vehicles');
  if (segment.kind === 'new') {
    return <VehicleCreate api={props.api} />;
  }
  if (segment.kind === 'detail') {
    return <VehicleDetail api={props.api} profileId={segment.id} />;
  }
  return <VehicleList api={props.api} />;
}

function VehicleList(props: { readonly api: AdminApi }): ReactElement {
  const [rows, setRows] = useState<VehicleProfile[]>([]);
  const [draft, setDraft] = useState<VehicleListFilters>(vehicleFilterSession.draft);
  const [applied, setApplied] = useState<VehicleListFilters>(vehicleFilterSession.applied);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const seq = useRef(0);

  const reload = () => {
    const requestSeq = seq.current + 1;
    seq.current = requestSeq;
    setLoading(true);
    setError(null);
    void props.api
      .listVehicleProfiles()
      .then((list) => {
        if (seq.current !== requestSeq) {
          return;
        }
        setRows(Array.isArray(list) ? list : []);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        if (seq.current !== requestSeq) {
          return;
        }
        setError(failure);
        setLoading(false);
      });
  };

  useEffect(() => {
    reload();
  }, [props.api]);

  const applyFilters = () => {
    vehicleFilterSession.draft = draft;
    vehicleFilterSession.applied = draft;
    setApplied(draft);
  };

  const resetFilters = () => {
    const empty: VehicleListFilters = { query: '', status: 'ALL' };
    vehicleFilterSession.draft = empty;
    vehicleFilterSession.applied = empty;
    setDraft(empty);
    setApplied(empty);
  };

  const filtered = rows
    .filter((row) => {
      if (applied.status !== 'ALL' && row.status !== applied.status) {
        return false;
      }
      if (applied.query.trim().length === 0) {
        return true;
      }
      return (
        matchIncludes(row.registration_plate, applied.query) ||
        matchIncludes(row.vin, applied.query) ||
        matchIncludes(row.alternative_identifier, applied.query) ||
        matchIncludes(readVehicleModel(row.attributes), applied.query)
      );
    })
    .slice()
    .sort((a, b) => {
      const byLabel = compareRu(vehicleBusinessLabel(a), vehicleBusinessLabel(b));
      return byLabel !== 0 ? byLabel : compareRu(a.id, b.id);
    });

  return (
    <div className="pkg-a-page">
      <RegistryToolbar
        title="Транспортные средства и оборудование"
        description="Конкретные единицы техники, доступные для закрепления за подрядчиками"
        createLabel="Добавить ТС / оборудование"
        createPath="/admin/vehicles/new"
      />
      <FilterBar onApply={applyFilters} onReset={resetFilters}>
        <label htmlFor="vehicle_filter_query" className="pkg-a-filter-query">
          Поиск
          <input
            id="vehicle_filter_query"
            value={draft.query}
            onChange={(event) => setDraft({ ...draft, query: event.target.value })}
            placeholder="Госномер, VIN, идентификатор, марка/модель"
          />
        </label>
        <label htmlFor="vehicle_filter_status">
          Статус
          <select
            id="vehicle_filter_status"
            value={draft.status}
            onChange={(event) =>
              setDraft({ ...draft, status: event.target.value as MasterStatusFilter })
            }
          >
            <option value="ALL">Все</option>
            <option value="ACTIVE">Активные</option>
            <option value="ARCHIVED">В архиве</option>
          </select>
        </label>
      </FilterBar>
      <RegistryState
        loading={loading}
        error={error}
        empty={!loading && error === null && rows.length === 0}
        filteredEmpty={!loading && error === null && rows.length > 0 && filtered.length === 0}
        onRetry={reload}
        onResetFilters={resetFilters}
      />
      {!loading && error === null && filtered.length > 0 ? (
        <div className="table-wrap">
          <table className="data-table pkg-a-table">
            <thead>
              <tr>
                <th scope="col">Марка / модель</th>
                <th scope="col">Госномер / идентификатор</th>
                <th scope="col">VIN</th>
                <th scope="col">Последнее решение</th>
                <th scope="col">Статус</th>
                <th scope="col">Действие</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  <td>
                    <RegistryLink href={`/admin/vehicles/${row.id}`}>
                      {vehicleModelRegistryLabel(row)}
                    </RegistryLink>
                  </td>
                  <td>{vehicleBusinessLabel(row)}</td>
                  <td>{displayOrDash(row.vin)}</td>
                  <td>{vehicleLastDecisionLabel(row)}</td>
                  <td>
                    <StatusChip
                      label={masterStatusLabel(row.status)}
                      tone={statusToneClass(row.status)}
                    />
                  </td>
                  <td>
                    <RegistryLink href={`/admin/vehicles/${row.id}`}>Открыть</RegistryLink>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <p className="meta pkg-a-hint">
        Виды ТС / спецтехники ведутся в разделе «Справочники», а не в этом реестре экземпляров.
      </p>
    </div>
  );
}

function VehicleCreate(props: { readonly api: AdminApi }): ReactElement {
  const [plate, setPlate] = useState('');
  const [model, setModel] = useState('');
  const [vin, setVin] = useState('');
  const [color, setColor] = useState('');
  const [alt, setAlt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const mounted = useRef(true);
  const dirty =
    plate.length > 0 || model.length > 0 || vin.length > 0 || color.length > 0 || alt.length > 0;
  useUnsavedLeaveGuard(dirty && !busy);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return (
    <div className="pkg-a-page">
      <button type="button" className="back-link" onClick={() => guardedNavigate('/admin/vehicles')}>
        ← К транспортным средствам
      </button>
      <h1>Новое ТС / оборудование</h1>
      {error !== null ? <PackageProblemAlert failure={error} /> : null}
      <form
        className="pkg-a-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (busy) {
            return;
          }
          setBusy(true);
          setError(null);
          const attributes = writeVehicleColor(writeVehicleModel({}, model), color);
          void props.api
            .createVehicleProfile({
              registration_plate: emptyToNull(plate),
              vin: emptyToNull(vin),
              alternative_identifier: emptyToNull(alt),
              attributes,
            })
            .then((created) => {
              if (!mounted.current) {
                return;
              }
              navigate(`/admin/vehicles/${created.id}`);
            })
            .catch((failure: unknown) => {
              if (!mounted.current) {
                return;
              }
              setError(failure);
              setBusy(false);
            });
        }}
      >
        <fieldset className="pkg-a-fieldset">
          <legend>Идентификация</legend>
          <div className="pkg-a-form-grid">
            <label htmlFor="vehicle_plate">
              Государственный регистрационный номер
              <input
                id="vehicle_plate"
                value={plate}
                onChange={(event) => setPlate(event.target.value)}
              />
            </label>
            <label htmlFor="vehicle_vin">
              VIN
              <input id="vehicle_vin" value={vin} onChange={(event) => setVin(event.target.value)} />
            </label>
            <label htmlFor="vehicle_alt" className="pkg-a-span-2">
              Другой идентификатор
              <input
                id="vehicle_alt"
                value={alt}
                onChange={(event) => setAlt(event.target.value)}
              />
            </label>
          </div>
        </fieldset>
        <fieldset className="pkg-a-fieldset">
          <legend>Описание</legend>
          <div className="pkg-a-form-grid">
            <label htmlFor="vehicle_model">
              Марка / модель
              <input
                id="vehicle_model"
                value={model}
                onChange={(event) => setModel(event.target.value)}
              />
            </label>
            <label htmlFor="vehicle_color">
              Цвет
              <input
                id="vehicle_color"
                value={color}
                onChange={(event) => setColor(event.target.value)}
              />
            </label>
          </div>
        </fieldset>
        <FormActions
          primaryLabel="Создать"
          busy={busy}
          onCancel={() => guardedNavigate('/admin/vehicles')}
        />
      </form>
    </div>
  );
}

function VehicleDetail(props: {
  readonly api: AdminApi;
  readonly profileId: string;
}): ReactElement {
  const [profile, setProfile] = useState<VehicleProfile | null>(null);
  const [plate, setPlate] = useState('');
  const [model, setModel] = useState('');
  const [vin, setVin] = useState('');
  const [color, setColor] = useState('');
  const [alt, setAlt] = useState('');
  const [duplicates, setDuplicates] = useState<DuplicateCandidate<VehicleProfile>[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const loadSeq = useRef(0);

  const applyProfile = (next: VehicleProfile) => {
    setProfile(next);
    setPlate(next.registration_plate ?? '');
    setModel(readVehicleModel(next.attributes));
    setVin(next.vin ?? '');
    setColor(readVehicleColor(next.attributes));
    setAlt(next.alternative_identifier ?? '');
  };

  const dirty =
    profile !== null &&
    (plate !== (profile.registration_plate ?? '') ||
      model !== readVehicleModel(profile.attributes) ||
      vin !== (profile.vin ?? '') ||
      color !== readVehicleColor(profile.attributes) ||
      alt !== (profile.alternative_identifier ?? ''));
  useUnsavedLeaveGuard(dirty);

  useEffect(() => {
    const seq = loadSeq.current + 1;
    loadSeq.current = seq;
    const targetId = props.profileId;
    setProfile(null);
    setError(null);
    setDuplicates([]);
    setNotFound(false);
    setLoading(true);
    setSaved(false);
    void props.api
      .getVehicleProfile(targetId)
      .then((next) => {
        if (loadSeq.current !== seq) {
          return;
        }
        applyProfile(next);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        if (loadSeq.current !== seq) {
          return;
        }
        if (isNotFound(failure)) {
          setNotFound(true);
        } else {
          setError(failure);
        }
        setLoading(false);
      });
    void props.api
      .vehicleDuplicates(targetId)
      .then((rows) => {
        if (loadSeq.current !== seq) {
          return;
        }
        setDuplicates(Array.isArray(rows) ? rows : []);
      })
      .catch((failure: unknown) => {
        if (loadSeq.current !== seq) {
          return;
        }
        if (!isNotFound(failure)) {
          setError(failure);
        }
      });
  }, [props.api, props.profileId]);

  if (loading) {
    return <p role="status">Загрузка…</p>;
  }
  if (notFound) {
    return <NotFoundCard listPath="/admin/vehicles" listLabel="К списку" />;
  }
  if (profile === null) {
    return error !== null ? <PackageProblemAlert failure={error} /> : <p role="status">Загрузка…</p>;
  }

  const resetDraft = () => {
    if (dirty && !window.confirm(getUnsavedLeaveMessage())) {
      return;
    }
    applyProfile(profile);
    setError(null);
    setSaved(false);
  };

  return (
    <div className="pkg-a-page">
      <button type="button" className="back-link" onClick={() => guardedNavigate('/admin/vehicles')}>
        ← К транспортным средствам
      </button>
      <div className="pkg-a-detail-header">
        <h1>{vehicleIdentityLabel(profile)}</h1>
        <StatusChip
          label={masterStatusLabel(profile.status)}
          tone={statusToneClass(profile.status)}
        />
      </div>
      <section className="pkg-a-readonly-block" aria-label="Последнее подписанное решение">
        <h2 className="pkg-a-section-title">Последнее подписанное решение</h2>
        <p>
          <strong>{vehicleLastDecisionLabel(profile)}</strong>
          {profile.last_signed_decision?.case_number
            ? ` · дело ${profile.last_signed_decision.case_number}`
            : null}
          {profile.last_signed_decision?.decided_at
            ? ` · ${new Date(profile.last_signed_decision.decided_at).toLocaleString('ru-RU')}`
            : null}
        </p>
        <p className="meta">
          {profile.last_signed_decision?.case_id
            ? `Идентификатор дела: ${profile.last_signed_decision.case_id}`
            : 'Нет связанного подписанного итога.'}{' '}
          Проекция не является текущим допуском на объект; ACTIVE/ARCHIVED отделён.
        </p>
      </section>
      {error !== null ? <PackageProblemAlert failure={error} /> : null}
      {saved ? (
        <SuccessBanner>
          <p>Изменения сохранены</p>
        </SuccessBanner>
      ) : null}
      <form
        className="pkg-a-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (busy) {
            return;
          }
          const targetId = profile.id;
          const expected = profile.version;
          const seq = loadSeq.current;
          const preserved = attributesPreserved(profile.attributes, null);
          const attributes = writeVehicleColor(writeVehicleModel(preserved, model), color);
          setBusy(true);
          setSaved(false);
          setError(null);
          void props.api
            .updateVehicleProfile(targetId, {
              expected_version: expected,
              registration_plate: emptyToNull(plate),
              vin: emptyToNull(vin),
              alternative_identifier: emptyToNull(alt),
              attributes,
            })
            .then((next) => {
              if (loadSeq.current !== seq) {
                return;
              }
              applyProfile(next);
              setSaved(true);
              setBusy(false);
            })
            .catch((failure: unknown) => {
              if (loadSeq.current !== seq) {
                return;
              }
              setError(failure);
              setBusy(false);
            });
        }}
      >
        <fieldset className="pkg-a-fieldset">
          <legend>Идентификация</legend>
          <div className="pkg-a-form-grid">
            <label htmlFor="v_plate">
              Государственный регистрационный номер
              <input id="v_plate" value={plate} onChange={(event) => setPlate(event.target.value)} />
            </label>
            <label htmlFor="v_vin">
              VIN
              <input id="v_vin" value={vin} onChange={(event) => setVin(event.target.value)} />
            </label>
            <label htmlFor="v_alt" className="pkg-a-span-2">
              Другой идентификатор
              <input id="v_alt" value={alt} onChange={(event) => setAlt(event.target.value)} />
            </label>
          </div>
        </fieldset>
        <fieldset className="pkg-a-fieldset">
          <legend>Описание</legend>
          <div className="pkg-a-form-grid">
            <label htmlFor="v_model">
              Марка / модель
              <input id="v_model" value={model} onChange={(event) => setModel(event.target.value)} />
            </label>
            <label htmlFor="v_color">
              Цвет
              <input id="v_color" value={color} onChange={(event) => setColor(event.target.value)} />
            </label>
          </div>
        </fieldset>
        <FormActions
          primaryLabel="Сохранить изменения"
          busy={busy}
          cancelLabel="Отменить изменения"
          onCancel={resetDraft}
          archiveLabel={profile.status !== 'ARCHIVED' ? 'Архивировать' : undefined}
          archiveBusy={archiveBusy}
          onArchive={
            profile.status !== 'ARCHIVED'
              ? () => {
                  if (
                    !window.confirm(
                      `Архивировать «${vehicleIdentityLabel(profile)}»? Восстановление из этого экрана недоступно.`,
                    )
                  ) {
                    return;
                  }
                  const targetId = profile.id;
                  const expected = profile.version;
                  const seq = loadSeq.current;
                  setArchiveBusy(true);
                  setError(null);
                  void props.api
                    .archiveVehicleProfile(targetId, expected)
                    .then((next) => {
                      if (loadSeq.current !== seq) {
                        return;
                      }
                      applyProfile(next);
                      setArchiveBusy(false);
                    })
                    .catch((failure: unknown) => {
                      if (loadSeq.current !== seq) {
                        return;
                      }
                      setError(failure);
                      setArchiveBusy(false);
                    });
                }
              : undefined
          }
        />
      </form>

      {duplicates.length > 0 ? (
        <section className="pkg-a-section" aria-labelledby="vehicle-duplicates-title">
          <h2 id="vehicle-duplicates-title">Возможные дубликаты</h2>
          <ul>
            {duplicates.map((row) => (
              <li key={row.profile.id}>
                <button
                  type="button"
                  className="table-link"
                  onClick={() => navigate(`/admin/vehicles/${row.profile.id}`)}
                >
                  {vehicleIdentityLabel(row.profile)}
                </button>{' '}
                · {duplicateReasonLabel(row.matched_fields)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <p className="meta pkg-a-hint">
        Список подрядчиков для этого ТС здесь не формируется: управление связями выполняется в
        карточке подрядчика.{' '}
        <button type="button" className="table-link" onClick={() => navigate('/admin/contractors')}>
          К подрядчикам
        </button>
      </p>
    </div>
  );
}
